import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, Filter, Search, List, Grid, Mail, Archive, Megaphone, Download, Upload, ChevronDown, Calendar, DollarSign, TrendingUp, Users, BarChart, Eye, Send, Target, ChevronLeft, ChevronRight, BarChart3, Settings, MessageSquare, Zap, Globe, MapPin, Home, Map, Edit3, Edit, UserPlus, UserMinus, Trash2, ArrowLeft, Loader2, FileText, Tag } from 'lucide-react';
import EmptyState from '../components/EmptyState';
import GoogleMapsLoader from '../components/GoogleMapsLoader';
import SelectResourceModal from '../components/manage/SelectResourceModal';
import EmailTemplateBuilder from '../components/EmailTemplateBuilder';
import { BusinessMapService, BusinessHomePin } from '../services/businessMapService';
import { addressMarkerService, AddressMarker } from '../services/addressMarkerService';
import { geofenceService, Geofence } from '../services/geofenceService';
import { emailTemplateService, EmailTemplate } from '../services/emailTemplateService';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../hooks/useAuth';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { supabase } from '../lib/supabase';
import LeadForms from './marketing/LeadForms';
import DiscountCodes from './marketing/DiscountCodes';
import OnlineBookings from './marketing/OnlineBookings';
import Campaigns from './marketing/Campaigns';

// Declare global Google Maps interface
declare global {
  interface Window {
    google: any;
  }
}

const statusOptions = [
  { label: 'All Campaigns', value: 'all' },
  { label: 'Active', value: 'active' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Draft', value: 'draft' },
  { label: 'Completed', value: 'completed' }
];

// Marketing sub-pages configuration
const marketingPages = [
  {
    id: 'email-campaigns',
    label: 'Email Campaigns',
    icon: Mail,
    description: 'Create and manage email marketing campaigns'
  },
  {
    id: 'email-templates',
    label: 'Email Templates',
    icon: Mail,
    description: 'Create and manage email templates'
  },
  {
    id: 'lead-forms',
    label: 'Lead Forms',
    icon: FileText,
    description: 'Create and manage lead capture forms'
  },
  {
    id: 'discount-codes',
    label: 'Discount Codes',
    icon: Tag,
    description: 'Create and manage promotional discount codes'
  },
  {
    id: 'online-bookings',
    label: 'Online Bookings',
    icon: Globe,
    description: 'Configure online booking settings and popup banners'
  },
  {
    id: 'sales-map',
    label: 'Sales Map',
    icon: MapPin,
    description: 'Geographic sales tracking and visualization'
  }
];

// Sample data for demonstration
const sampleCampaigns = [
  {
    id: '1',
    name: 'Summer Promotion 2024',
    type: 'Email Campaign',
    status: 'active',
    reach: 2500,
    clicks: 120,
    conversions: 8,
    budget: 500,
    created_at: '2024-01-15',
  },
  {
    id: '2',
    name: 'Spring Cleaning Special',
    type: 'Social Media',
    status: 'completed',
    reach: 3200,
    clicks: 180,
    conversions: 15,
    budget: 750,
    created_at: '2024-01-10',
  },
  {
    id: '3',
    name: 'New Customer Discount',
    type: 'Email Campaign',
    status: 'scheduled',
    reach: 0,
    clicks: 0,
    conversions: 0,
    budget: 300,
    created_at: '2024-01-20',
  }
];

// Component for different marketing pages
function CampaignsContent() {
  const [selectedCampaigns, setSelectedCampaigns] = useState(new Set());
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  
  const campaigns = sampleCampaigns;

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (selectedStatus.value !== 'all' && campaign.status !== selectedStatus.value) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
  return (
      campaign.name.toLowerCase().includes(searchLower) ||
      campaign.type.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'scheduled':
        return 'bg-blue-100 text-blue-700';
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      case 'completed':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const calculateConversionRate = (conversions: number, clicks: number) => {
    if (clicks === 0) return 0;
    return ((conversions / clicks) * 100).toFixed(1);
  };

  // Calculate stats from campaigns
  const totalActiveCampaigns = campaigns.filter(c => c.status === 'active').length;
  const totalReach = campaigns.reduce((sum, c) => sum + c.reach, 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + c.clicks, 0);
  const totalConversions = campaigns.reduce((sum, c) => sum + c.conversions, 0);
  const overallConversionRate = calculateConversionRate(totalConversions, totalClicks);
  const scheduledCampaigns = campaigns.filter(c => c.status === 'scheduled').length;

  return (
    <>
      {/* Stats Overview */}
      <div className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 p-6 shadow-lg" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
          <div className="flex items-center justify-between">
            <div>
                <p className="text-sm text-gray-600 font-medium">Active Campaigns</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{totalActiveCampaigns}</p>
            </div>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                <Megaphone className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>

          <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 p-6 shadow-lg" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
          <div className="flex items-center justify-between">
            <div>
                <p className="text-sm text-gray-600 font-medium">Total Reach</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{totalReach.toLocaleString()}</p>
            </div>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                <Users className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>

          <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 p-6 shadow-lg" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
          <div className="flex items-center justify-between">
            <div>
                <p className="text-sm text-gray-600 font-medium">Conversion Rate</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{overallConversionRate}%</p>
            </div>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                <Target className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>

          <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 p-6 shadow-lg" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
          <div className="flex items-center justify-between">
            <div>
                <p className="text-sm text-gray-600 font-medium">Scheduled</p>
                <p className="mt-2 text-2xl font-bold text-gray-900">{scheduledCampaigns}</p>
            </div>
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                <Calendar className="h-6 w-6 text-white" />
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search Section */}
      <div className="bg-white/90 backdrop-blur-xl border border-gray-200/30 rounded-2xl p-4 mb-6" style={{
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)'
      }}>
        <div className="flex flex-col space-y-4 md:space-y-0 md:flex-row md:justify-between md:items-center">
          {/* Status Filter */}
          <div className="relative w-full sm:w-auto">
            <button
              onClick={() => setIsStatusOpen(!isStatusOpen)}
              className="w-full sm:w-auto inline-flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-900 bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-purple-500/20 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
              style={{ minHeight: '44px' }}
            >
              <span>{selectedStatus.label}</span>
              <ChevronDown
                className={`ml-2 h-5 w-5 transition-transform duration-300 ${
                  isStatusOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            
            {isStatusOpen && (
              <div className="absolute left-0 mt-2 w-full sm:w-56 rounded-2xl shadow-2xl bg-white/95 backdrop-blur-xl border border-gray-200/50 z-50 transform transition-all duration-300 ease-out animate-in slide-in-from-top-2 fade-in-0" style={{
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)'
              }}>
                <div className="py-2" role="menu" aria-orientation="vertical">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSelectedStatus(option);
                        setIsStatusOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm transition-all duration-200 ease-out touch-manipulation ${
                        selectedStatus.value === option.value
                          ? 'bg-purple-50 text-purple-900 font-semibold'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      role="menuitem"
                      style={{ minHeight: '44px' }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Bar */}
          <div className="w-full md:w-auto md:min-w-80">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 text-base bg-white/80 backdrop-blur-sm border border-gray-300/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500/50 transition-all duration-300 ease-out shadow-sm touch-manipulation"
                style={{ 
                  minHeight: '44px',
                  fontSize: '16px', // Prevent iOS zoom
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Campaigns Content */}
      <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg" style={{
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)'
      }}>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="w-12 h-12 border-3 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto shadow-lg shadow-purple-600/30"></div>
              <p className="mt-4 text-base text-gray-600 font-medium">Loading campaigns...</p>
            </div>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <EmptyState
              icon={<Megaphone className="w-8 h-8 text-gray-400" />}
              title={searchQuery ? 'No matching campaigns' : 'No campaigns yet'}
              description={
                searchQuery
                  ? 'Try adjusting your search terms'
                  : 'Start creating marketing campaigns to promote your business.'
              }
              actionLabel={searchQuery ? 'Clear search' : 'Create your first campaign'}
              onAction={() => searchQuery ? setSearchQuery('') : () => {}}
            />
          </div>
        ) : (
          <div className="p-6">
            <div className="space-y-4">
              {filteredCampaigns.map((campaign, index) => (
                <div 
                  key={campaign.id}
                  className="bg-white/90 backdrop-blur-xl rounded-xl border border-gray-200/50 shadow-lg hover:shadow-xl transition-all duration-300 ease-out transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer p-4"
                  style={{
                    backdropFilter: 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                    animationDelay: `${index * 50}ms`
                  }}
                >
                  <div className="flex items-start space-x-3">
                    {/* Campaign Icon */}
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center text-sm font-bold text-white shadow-sm">
                        <Megaphone className="h-5 w-5" />
                      </div>
                    </div>
                    
                    {/* Campaign Details */}
                    <div className="flex-1 min-w-0">
                      {/* Header Row */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-gray-900 truncate">
                            {campaign.name}
                          </h3>
                          <p className="text-xs text-gray-600 font-medium truncate">{campaign.type}</p>
                        </div>
                        
                        {/* Status Badge */}
                        <div className="flex-shrink-0 ml-3">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-lg shadow-sm ${getStatusColor(campaign.status)}`}>
                            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      
                      {/* Stats Row */}
                      <div className="grid grid-cols-4 gap-4 pt-2 border-t border-gray-200/50">
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Reach</p>
                          <p className="text-sm font-semibold text-gray-900">{campaign.reach.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Clicks</p>
                          <p className="text-sm font-semibold text-gray-900">{campaign.clicks}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Conv. Rate</p>
                          <p className="text-sm font-semibold text-gray-900">{calculateConversionRate(campaign.conversions, campaign.clicks)}%</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-gray-500">Budget</p>
                          <p className="text-sm font-semibold text-gray-900">${campaign.budget}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>


    </>
  );
}

// Email Templates component
function EmailTemplatesContent() {
  const { businessId, selectedBusiness } = useBusiness();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [templateToSend, setTemplateToSend] = useState<EmailTemplate | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Get business logo URL for template previews
  const logoUrl = selectedBusiness?.booking_settings?.logo_url;

  // Load templates on mount
  useEffect(() => {
    if (businessId) {
      loadTemplates();
    }
  }, [businessId]);

  const loadTemplates = async () => {
    if (!businessId) return;
    
    setIsLoading(true);
    try {
      const data = await emailTemplateService.getTemplates(businessId);
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsBuilderOpen(true);
  };

  const handleEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsBuilderOpen(true);
  };

  const handleSaveTemplate = async (template: any) => {
    if (!businessId) return;
    
    setIsSaving(true);
    try {
      if (editingTemplate) {
        // Update existing template
        await emailTemplateService.updateTemplate(editingTemplate.id, {
          name: template.name,
          content: template.content,
          category: template.category || 'Custom',
          status: template.status || 'draft'
        });
      } else {
        // Create new template
        await emailTemplateService.createTemplate(businessId, {
          name: template.name,
          content: template.content,
          category: template.category || 'Custom',
          status: 'draft',
          template_type: template.template_type
        });
      }
      
      // Reload templates
      await loadTemplates();
      setIsBuilderOpen(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (confirm('Are you sure you want to delete this template?')) {
      try {
        await emailTemplateService.deleteTemplate(templateId);
        await loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
        alert('Failed to delete template. Please try again.');
      }
    }
  };

  const handleSendTest = (template: EmailTemplate) => {
    setTemplateToSend(template);
    setTestEmail('');
    setShowSendModal(true);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim() || !templateToSend || !businessId) return;

    setIsSendingTest(true);
    try {
      // Render the template to HTML (similar to edge function logic)
      let htmlContent = '';
      
      if (templateToSend.content && templateToSend.content.sections) {
        const htmlParts = [];
        
        // Start HTML document
        htmlParts.push('<html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head><body style="font-family:Arial,sans-serif;margin:0;padding:20px;background:#f9fafb;">');
        htmlParts.push('<div style="max-width:600px;margin:0 auto;background:white;border-radius:8px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.1);">');
        
        // Process each section
        for (const section of templateToSend.content.sections) {
          if (section.type === 'header') {
            const bgColor = section.content?.backgroundColor || '#6366f1';
            const textColor = section.content?.textColor || '#ffffff';
            
            // Use text-only header for maximum email client compatibility
            let headerContent = '';
            if (section.content?.showBusinessName !== false) {
              const businessName = selectedBusiness?.name || 'Your Business';
              headerContent = `
                <div style="color:${textColor};font-size:28px;font-weight:bold;text-align:center;letter-spacing:1px;text-transform:uppercase;">
                  ${businessName}
                </div>`;
            }
            htmlParts.push(`<div style="background:${bgColor};color:${textColor};padding:30px;text-align:center;">
              ${headerContent}
            </div>`);
            
          } else if (section.type === 'hero') {
            const bgColor = section.content?.backgroundColor || '#f3f4f6';
            htmlParts.push(`<div style="background:${bgColor};padding:40px;text-align:center;">`);
            
            if (section.content?.image) {
              htmlParts.push(`<img src="${section.content.image}" style="max-width:100%;height:auto;margin-bottom:20px;border-radius:4px;" alt="Hero Image" />`);
            }
            
            if (section.content?.heading) {
              htmlParts.push(`<h1 style="font-size:28px;margin:0 0 16px 0;color:#1f2937;line-height:1.3;">${section.content.heading}</h1>`);
            }
            
            if (section.content?.subheading) {
              htmlParts.push(`<p style="font-size:18px;color:#6b7280;margin:0 0 20px 0;line-height:1.5;">${section.content.subheading}</p>`);
            }
            
            if (section.content?.buttonText) {
              const buttonUrl = section.content?.buttonUrl || '#';
              
              // Create bulletproof email button
              htmlParts.push(`
                <table border="0" cellpadding="0" cellspacing="0" style="margin:20px auto;">
                  <tr>
                    <td bgcolor="#6366f1" style="padding:0;border-radius:6px;">
                      <a href="${buttonUrl}" target="_blank" style="background-color:#6366f1;border:solid 1px #6366f1;border-radius:6px;color:#ffffff;display:inline-block;font-family:Arial,sans-serif;font-size:16px;font-weight:bold;line-height:20px;margin:0;padding:12px 24px;text-decoration:none;text-transform:none;">${section.content.buttonText}</a>
                    </td>
                  </tr>
                </table>
              `);
            }
            
            htmlParts.push('</div>');
            
          } else if (section.type === 'text') {
            if (section.content?.text) {
              htmlParts.push(`<div style="padding:20px;">
                <p style="font-size:16px;line-height:1.6;color:#374151;margin:0;">${section.content.text}</p>
              </div>`);
            }
            
          } else if (section.type === 'image') {
            if (section.content?.url) {
              htmlParts.push(`<div style="padding:20px;text-align:center;">
                <img src="${section.content.url}" style="max-width:100%;height:auto;border-radius:4px;" alt="Email Image" />
              </div>`);
            }
            
          } else if (section.type === 'button') {
            if (section.content?.text) {
              const buttonUrl = section.content?.url || '#';
              const backgroundColor = section.content?.backgroundColor || '#6366f1';
              const textColor = section.content?.color || '#ffffff';
              
              // Create bulletproof email button
              htmlParts.push(`<div style="padding:20px;text-align:center;">
                <table border="0" cellpadding="0" cellspacing="0" style="margin:0 auto;">
                  <tr>
                    <td bgcolor="${backgroundColor}" style="padding:0;border-radius:6px;">
                      <a href="${buttonUrl}" target="_blank" style="background-color:${backgroundColor};border:solid 1px ${backgroundColor};border-radius:6px;color:${textColor};display:inline-block;font-family:Arial,sans-serif;font-size:16px;font-weight:bold;line-height:20px;margin:0;padding:12px 24px;text-decoration:none;text-transform:none;">${section.content.text}</a>
                    </td>
                  </tr>
                </table>
              </div>`);
            }
            
          } else if (section.type === 'divider') {
            htmlParts.push(`<div style="padding:10px 20px;">
              <hr style="border:none;border-top:1px solid #e5e7eb;margin:0;" />
            </div>`);
            
          } else if (section.type === 'spacer') {
            const height = section.content?.height || 20;
            htmlParts.push(`<div style="height:${height}px;"></div>`);
          }
        }
        
        // Close HTML document
        htmlParts.push('</div></body></html>');
        
        htmlContent = htmlParts.join('');
      } else {
        htmlContent = '<p>Template content not available</p>';
      }

      // Send the email using the existing Gmail function
      const { data } = await supabase.functions.invoke('gmail-send-email', {
        body: {
          to: testEmail.trim(),
          subject: templateToSend.subject || 'Test Email Template',
          body: htmlContent,
          businessId: businessId
        }
      });

      if (data?.success) {
        alert('Test email sent successfully!');
        setShowSendModal(false);
        setTestEmail('');
        setTemplateToSend(null);
      } else {
        throw new Error('Failed to send email');
      }

    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Failed to send test email. Please try again.');
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <>
      <div className="h-full overflow-y-auto bg-gray-50">
        <div className="w-full">

          {/* Templates Container */}
          <div className="bg-white shadow-sm border-t border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Your Designs</h2>
              <button
                onClick={handleCreateTemplate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 flex items-center space-x-2 shadow-sm hover:shadow-md font-medium text-sm"
              >
                <Plus className="h-4 w-4" />
                 <span>Create Design</span>
              </button>
            </div>
            
            <div className="p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
                </div>
              ) : templates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => {
                      setEditingTemplate(template);
                      setIsBuilderOpen(true);
                    }}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-xl hover:border-blue-200 transition-all duration-300 group cursor-pointer"
                  >
                    {/* Template Preview */}
                    <div className="aspect-[4/3] relative">
                            {/* Render Full Template Preview */}
                            <div className="w-full h-full overflow-hidden">
                              {template.content && typeof template.content === 'object' && template.content.sections ? (
                                // Render structured template content
                                <div className="h-full flex flex-col">
                                  {template.content.sections.map((section: any, idx: number) => (
                                    <div key={idx} className="flex-shrink-0">
                                                                              {section.type === 'header' && (
                                          <div 
                                            className="px-4 py-3 text-center flex items-center justify-center"
                                            style={{ 
                                              backgroundColor: section.content?.backgroundColor || '#6366f1',
                                              color: section.content?.textColor || '#ffffff'
                                            }}
                                          >
                                            {logoUrl ? (
                                              <img 
                                                src={logoUrl} 
                                                alt={selectedBusiness?.name || 'Business Logo'} 
                                                className="h-6 object-contain"
                                                style={{ 
                                                  filter: section.content?.logoInvert !== false ? 'brightness(0) invert(1)' : 'none',
                                                  maxWidth: '120px'
                                                }}
                                              />
                                            ) : (
                                              <div className="text-sm font-bold">
                                                {selectedBusiness?.name || section.content?.businessName || 'Business Name'}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      
                                      {section.type === 'hero' && (
                                        <div 
                                          className="px-4 py-6 text-center flex-1"
                                          style={{ backgroundColor: section.content?.backgroundColor || '#f3f4f6' }}
                                        >
                                          {section.content?.image && (
                                            <div className="w-full h-12 bg-gray-300 rounded mb-3 mx-auto max-w-24"></div>
                                          )}
                                          {section.content?.heading && (
                                            <div className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                                              {section.content.heading}
                                            </div>
                                          )}
                                          {section.content?.subheading && (
                                            <div className="text-sm text-gray-600 mb-3 leading-tight">
                                              {section.content.subheading}
                                            </div>
                                          )}
                                          {section.content?.buttonText && (
                                            <div className="inline-block px-4 py-2 bg-blue-600 text-white rounded text-sm font-bold">
                                              {section.content.buttonText}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      
                                      {section.type === 'text' && section.content?.text && (
                                        <div className="px-4 py-3">
                                          <div className="text-sm text-gray-700 leading-tight line-clamp-3">
                                            {section.content.text}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {section.type === 'button' && section.content?.text && (
                                        <div className="px-4 py-3 text-center">
                                          <div 
                                            className="inline-block px-4 py-2 rounded text-sm font-bold"
                                            style={{ 
                                              backgroundColor: section.content?.backgroundColor || '#6366f1',
                                              color: section.content?.color || '#ffffff'
                                            }}
                                          >
                                            {section.content.text}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {section.type === 'image' && section.content?.url && (
                                        <div className="px-4 py-3">
                                          <div className="w-full h-16 bg-gray-200 rounded"></div>
                                        </div>
                                      )}
                                      
                                      {section.type === 'divider' && (
                                        <div className="px-4 py-2">
                                          <div className="w-full h-px bg-gray-300"></div>
                                        </div>
                                      )}
                                      
                                      {section.type === 'spacer' && (
                                        <div style={{ height: `${Math.min(section.content?.height || 20, 24)}px` }}></div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                // Default preview for templates without structured content
                                <div className="h-full flex flex-col">
                                  {/* Header */}
                                  <div className="bg-blue-600 px-4 py-3 text-center flex items-center justify-center">
                                    {logoUrl ? (
                                      <img 
                                        src={logoUrl} 
                                        alt={selectedBusiness?.name || 'Business Logo'} 
                                        className="h-6 object-contain"
                                        style={{ 
                                          filter: 'brightness(0) invert(1)',
                                          maxWidth: '120px'
                                        }}
                                      />
                                    ) : (
                                      <div className="text-sm text-white font-bold">
                                        {selectedBusiness?.name || 'Business Name'}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Content */}
                                  <div className="flex-1 px-4 py-6 bg-gray-50 text-center">
                                    <div className="text-lg font-bold text-gray-900 mb-2">
                                      {template.subject || template.name || 'Email Template'}
                                    </div>
                                    <div className="text-sm text-gray-600 mb-3">
                                      Email content preview
                                    </div>
                                    <div className="inline-block px-4 py-2 bg-blue-600 text-white rounded text-sm font-bold">
                                      View Details
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            
                    </div>

                    {/* Template Info */}
                    <div className="p-5 border-t border-gray-100">
                      <div className="mb-4">
                        <h3 className="font-semibold text-gray-900 truncate mb-2 text-lg">
                          {template.name}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {template.subject || template.content?.subject || 'No subject'}
                        </p>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div className="text-xs text-gray-500 font-medium">
                          {new Date(template.created_at || '').toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTemplateToSend(template);
                              setShowSendModal(true);
                            }}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1.5 text-xs font-medium"
                            title="Send Test Email"
                          >
                            <Mail className="h-3 w-3" />
                            <span>Send</span>
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTemplate(template.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Template"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              ) : (
                /* Empty State */
                <div className="text-center py-16 px-4">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <Mail className="h-10 w-10 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">No email templates yet</h3>
                    <p className="text-gray-600 mb-8 leading-relaxed">Create your first email template to streamline your communications and save time on future campaigns.</p>
                    <button
                      onClick={handleCreateTemplate}
                      className="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-semibold touch-manipulation flex items-center space-x-2 mx-auto"
                      style={{ minHeight: '44px' }}
                    >
                      <Plus className="h-5 w-5" />
                      <span>Create Your First Template</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email Template Builder Modal */}
      <EmailTemplateBuilder
        isOpen={isBuilderOpen}
        onClose={() => {
          setIsBuilderOpen(false);
          setEditingTemplate(null);
        }}
        onSave={handleSaveTemplate}
        initialTemplate={editingTemplate}
      />

      {/* Send Test Email Modal */}
      {showSendModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)'
          }}
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Send className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Send Test Email</h3>
                <p className="text-xs sm:text-sm text-gray-600 truncate">Test template: {templateToSend?.name}</p>
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter email address to test..."
                className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
                style={{ minHeight: '44px', fontSize: '16px' }}
                disabled={isSendingTest}
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowSendModal(false);
                  setTestEmail('');
                  setTemplateToSend(null);
                }}
                className="w-full sm:w-auto px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
                style={{ minHeight: '44px' }}
                disabled={isSendingTest}
              >
                <span className="text-sm sm:text-base">Cancel</span>
              </button>
              <button
                onClick={handleSendTestEmail}
                disabled={!testEmail.trim() || isSendingTest}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 flex items-center justify-center space-x-2 touch-manipulation"
                style={{ minHeight: '44px' }}
              >
                {isSendingTest ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Send Test</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Sales Map component
function SalesMapContent() {
  const mapRef = useRef<HTMLDivElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [map, setMap] = useState<any>(null);
  const [homePin, setHomePin] = useState<{lat: number, lng: number} | null>(null);
  const [showHomePinModal, setShowHomePinModal] = useState(false);
  const [homeMarker, setHomeMarker] = useState<any>(null);
  const [autocomplete, setAutocomplete] = useState<any>(null);
  const [selectedAddress, setSelectedAddress] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [homePinLoaded, setHomePinLoaded] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Address marker functionality
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [clickedLocation, setClickedLocation] = useState<{lat: number, lng: number} | null>(null);
  const [addressMarkers, setAddressMarkers] = useState<{marker: any, data: AddressMarker}[]>([]);
  const [isLoadingMarkers, setIsLoadingMarkers] = useState(false);
  
  // Edit marker functionality
  const [showEditMarkerModal, setShowEditMarkerModal] = useState(false);
  const [selectedMarkerForEdit, setSelectedMarkerForEdit] = useState<{marker: any, data: AddressMarker} | null>(null);
  const [isUpdatingMarker, setIsUpdatingMarker] = useState(false);
  const [isDeletingMarker, setIsDeletingMarker] = useState(false);
  
  // Geofencing functionality
  const [isGeofencingMode, setIsGeofencingMode] = useState(false);
  const [geofencePoints, setGeofencePoints] = useState<{lat: number, lng: number}[]>([]);
  const [geofenceMarkers, setGeofenceMarkers] = useState<any[]>([]);
  const [geofencePolygon, setGeofencePolygon] = useState<any>(null);
  const [geofenceName, setGeofenceName] = useState<string>('');
  const [isEditingGeofenceName, setIsEditingGeofenceName] = useState(false);
  const [geofenceCompleted, setGeofenceCompleted] = useState(false);
  const [savedGeofences, setSavedGeofences] = useState<Geofence[]>([]);
  const [loadedGeofencePolygons, setLoadedGeofencePolygons] = useState<any[]>([]);
  const [isSavingGeofence, setIsSavingGeofence] = useState(false);
  const [showGeofencesModal, setShowGeofencesModal] = useState(false);
  const [showEditGeofenceModal, setShowEditGeofenceModal] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<Geofence | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [showSelectResourceModal, setShowSelectResourceModal] = useState(false);
  const [isAddingResource, setIsAddingResource] = useState(false);
  const [isRemovingResource, setIsRemovingResource] = useState(false);
  
  // Get real business and user IDs from context
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const businessId = selectedBusiness?.id;
  const userId = user?.id;

  // Handle home icon click
  const handleHomeClick = () => {
    if (homePin && map) {
      // Navigate to home pin
      map.panTo(homePin);
      map.setZoom(15);
    } else {
      // Open modal to set home pin
      setSelectedAddress('');
      setShowHomePinModal(true);
    }
  };

  // Handle address marker placement
  const handlePlaceAddressMarker = async (iconType: string) => {
    if (!clickedLocation || !map || !window.google?.maps || !businessId || !userId) return;

    setIsLoadingMarkers(true);

    // Define marker icons
    const markerIcons = {
      'not-home': {
        bgColor: '#F3F4F6',
        borderColor: '#9CA3AF',
        iconColor: '#6B7280',
        title: 'Not Home'
      },
      'booked': {
        bgColor: '#DCFCE7',
        borderColor: '#16A34A',
        iconColor: '#16A34A',
        title: 'Booked'
      },
      'comeback-later': {
        bgColor: '#FEF3C7',
        borderColor: '#D97706',
        iconColor: '#D97706',
        title: 'Comeback Later'
      },
      'interested': {
        bgColor: '#DBEAFE',
        borderColor: '#2563EB',
        iconColor: '#2563EB',
        title: 'Interested'
      },
      'estimate-sent': {
        bgColor: '#D1FAE5',
        borderColor: '#059669',
        iconColor: '#059669',
        title: 'Estimate Sent'
      },
      'bad-customer': {
        bgColor: '#FEE2E2',
        borderColor: '#DC2626',
        iconColor: '#DC2626',
        title: 'Bad Customer'
      },
      'customer': {
        bgColor: '#E0F2FE',
        borderColor: '#0284C7',
        iconColor: '#0284C7',
        title: 'Customer'
      }
    };

    const selectedIcon = markerIcons[iconType as keyof typeof markerIcons];
    if (!selectedIcon) {
      setIsLoadingMarkers(false);
      return;
    }

    // Create marker request
    const markerRequest = {
      business_id: businessId,
      latitude: clickedLocation.lat,
      longitude: clickedLocation.lng,
      marker_type: iconType as AddressMarker['marker_type'],
      created_by: userId
    };

    // Save to database first
    const savedMarker = await addressMarkerService.createMarker(markerRequest);
    
    if (!savedMarker) {
      console.error('Failed to save address marker to database');
      setIsLoadingMarkers(false);
      return;
    }

    // Create marker with custom icon
    const googleMarker = new window.google.maps.Marker({
      position: clickedLocation,
      map: map,
      title: selectedIcon.title,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
              </filter>
            </defs>
            <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="${selectedIcon.bgColor}" stroke="${selectedIcon.borderColor}" stroke-width="1" filter="url(#shadow)"/>
            <g transform="translate(5, 5)">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="${selectedIcon.iconColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="${selectedIcon.iconColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              ${iconType === 'not-home' ? '' : 
                iconType === 'booked' ? 
                  `<circle cx="18" cy="5" r="7" fill="${selectedIcon.iconColor}"/><path d="M14.5,5 L16,6.5 L21.5,2" stroke="white" stroke-width="3.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>` :
                iconType === 'comeback-later' ?
                  `<circle cx="18" cy="5" r="7" fill="${selectedIcon.iconColor}"/><circle cx="18" cy="5" r="5.5" fill="none" stroke="white" stroke-width="3"/><path d="M18,2 L18,5 L21,8" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                iconType === 'interested' ?
                  `<circle cx="18" cy="5" r="7" fill="${selectedIcon.iconColor}"/><circle cx="18" cy="2" r="2.5" fill="white"/><path d="M15,8 Q18,11 21,8" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                iconType === 'estimate-sent' ?
                  `<circle cx="18" cy="5" r="7" fill="${selectedIcon.iconColor}"/><rect x="14" y="2" width="8" height="6" fill="white" stroke="white" stroke-width="1" rx="1"/><path d="M15,3.5 L21,3.5 M15,5 L21,5 M15,6.5 L19,6.5" stroke="${selectedIcon.iconColor}" stroke-width="1.5" stroke-linecap="round"/>` :
                iconType === 'customer' ?
                  `<circle cx="18" cy="5" r="7" fill="${selectedIcon.iconColor}"/><circle cx="18" cy="2.5" r="2.5" fill="white"/><path d="M12,10 Q18,6 24,10" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                  `<circle cx="18" cy="5" r="7" fill="${selectedIcon.iconColor}"/><path d="M14,3 Q18,1 22,3 Q22,5 18,7 Q14,5 14,3 Z" fill="white"/><circle cx="16" cy="3.5" r="0.8" fill="${selectedIcon.iconColor}"/><circle cx="20" cy="3.5" r="0.8" fill="${selectedIcon.iconColor}"/><path d="M15,8 L17,6 L19,6 L21,8" stroke="white" stroke-width="2" stroke-linecap="round"/>`
              }
            </g>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 32),
      },
      zIndex: 100
    });

    // Add click listener to marker for editing
    googleMarker.addListener('click', () => {
      handleMarkerClick({ marker: googleMarker, data: savedMarker });
    });

    // Store marker with database data
    setAddressMarkers(prev => [...prev, {
      marker: googleMarker,
      data: savedMarker
    }]);

    // Close modal and reset
    setShowAddressModal(false);
    setClickedLocation(null);
    setIsLoadingMarkers(false);
    
    console.log(`Placed ${selectedIcon.title} marker at:`, clickedLocation, 'Database ID:', savedMarker.id);
  };

  // Handle marker click for editing
  const handleMarkerClick = (markerData: {marker: any, data: AddressMarker}) => {
    setSelectedMarkerForEdit(markerData);
    setShowEditMarkerModal(true);
  };

  // Handle updating an existing marker
  const handleUpdateMarker = async (newMarkerType: AddressMarker['marker_type']) => {
    if (!selectedMarkerForEdit || !businessId) return;

    setIsUpdatingMarker(true);

    try {
            const updated = await addressMarkerService.updateMarker(
        selectedMarkerForEdit.data.id!, 
        { marker_type: newMarkerType }
      );

      if (updated) {
        // Update the marker data
        const updatedMarkerData = { ...selectedMarkerForEdit.data, marker_type: newMarkerType };
        
        // Remove old marker from map
        selectedMarkerForEdit.marker.setMap(null);

        // Define marker icons
        const markerIcons = {
          'not-home': { bgColor: '#F3F4F6', borderColor: '#9CA3AF', iconColor: '#6B7280', title: 'Not Home' },
          'booked': { bgColor: '#DCFCE7', borderColor: '#16A34A', iconColor: '#16A34A', title: 'Booked' },
          'comeback-later': { bgColor: '#FEF3C7', borderColor: '#D97706', iconColor: '#D97706', title: 'Comeback Later' },
          'interested': { bgColor: '#DBEAFE', borderColor: '#2563EB', iconColor: '#2563EB', title: 'Interested' },
          'estimate-sent': { bgColor: '#F7FEE7', borderColor: '#84CC16', iconColor: '#84CC16', title: 'Estimate Sent' },
          'bad-customer': { bgColor: '#FEE2E2', borderColor: '#DC2626', iconColor: '#DC2626', title: 'Bad Customer' },
          'customer': { bgColor: '#E0F2FE', borderColor: '#0284C7', iconColor: '#0284C7', title: 'Customer' }
        };

        const iconConfig = markerIcons[newMarkerType];

        // Create new marker with updated type
        const newGoogleMarker = new window.google.maps.Marker({
          position: { lat: updatedMarkerData.latitude, lng: updatedMarkerData.longitude },
          map: map,
          title: iconConfig.title,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                <defs>
                  <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
                  </filter>
                </defs>
                <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="${iconConfig.bgColor}" stroke="${iconConfig.borderColor}" stroke-width="1" filter="url(#shadow)"/>
                <g transform="translate(5, 5)">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="${iconConfig.iconColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="${iconConfig.iconColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  ${newMarkerType === 'not-home' ? '' : 
                    newMarkerType === 'booked' ? 
                      `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><path d="M14.5,5 L16,6.5 L21.5,2" stroke="white" stroke-width="3.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>` :
                    newMarkerType === 'comeback-later' ?
                      `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><circle cx="18" cy="5" r="5.5" fill="none" stroke="white" stroke-width="3"/><path d="M18,2 L18,5 L21,8" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                    newMarkerType === 'interested' ?
                      `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><circle cx="18" cy="2" r="2.5" fill="white"/><path d="M15,8 Q18,11 21,8" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                    newMarkerType === 'estimate-sent' ?
                      `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><rect x="14" y="2" width="8" height="6" fill="white" stroke="white" stroke-width="1" rx="1"/><path d="M15,3.5 L21,3.5 M15,5 L21,5 M15,6.5 L19,6.5" stroke="${iconConfig.iconColor}" stroke-width="1.5" stroke-linecap="round"/>` :
                    newMarkerType === 'customer' ?
                      `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><circle cx="18" cy="2.5" r="2.5" fill="white"/><path d="M12,10 Q18,6 24,10" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                      `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><path d="M14,3 Q18,1 22,3 Q22,5 18,7 Q14,5 14,3 Z" fill="white"/><circle cx="16" cy="3.5" r="0.8" fill="${iconConfig.iconColor}"/><circle cx="20" cy="3.5" r="0.8" fill="${iconConfig.iconColor}"/><path d="M15,8 L17,6 L19,6 L21,8" stroke="white" stroke-width="2" stroke-linecap="round"/>`
                  }
                </g>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 32),
          },
          zIndex: 100
        });

        // Add click listener to new marker
        newGoogleMarker.addListener('click', () => {
          handleMarkerClick({ marker: newGoogleMarker, data: updatedMarkerData });
        });

        // Update markers array
        setAddressMarkers(prev => prev.map(item => 
          item.data.id === selectedMarkerForEdit.data.id 
            ? { marker: newGoogleMarker, data: updatedMarkerData }
            : item
        ));

        console.log(`Updated marker to ${iconConfig.title} for ID:`, updated.id);
      } else {
        console.error('Failed to update marker in database');
      }
    } catch (error) {
      console.error('Error updating marker:', error);
    } finally {
      setIsUpdatingMarker(false);
      setShowEditMarkerModal(false);
      setSelectedMarkerForEdit(null);
    }
  };

  // Handle deleting a marker
  const handleDeleteMarker = async () => {
    if (!selectedMarkerForEdit) return;

    setIsDeletingMarker(true);

    try {
      await addressMarkerService.deleteMarker(selectedMarkerForEdit.data.id!);

      // Remove marker from map
      selectedMarkerForEdit.marker.setMap(null);

      // Remove from state
      setAddressMarkers(prev => prev.filter(item => item.data.id !== selectedMarkerForEdit.data.id));

      console.log('Deleted marker with ID:', selectedMarkerForEdit.data.id);
    } catch (error) {
      console.error('Error deleting marker:', error);
    } finally {
      setIsDeletingMarker(false);
      setShowEditMarkerModal(false);
      setSelectedMarkerForEdit(null);
    }
  };

  // Handle setting home pin
  const handleSetHomePin = async (location: {lat: number, lng: number}) => {
    setIsLoading(true);
    
    // Always set the pin locally first
    setHomePin(location);
    
    // Remove existing home marker
    if (homeMarker) {
      homeMarker.setMap(null);
    }
    
    // Add new home marker with house icon
    if (map && window.google?.maps) {
      const marker = new window.google.maps.Marker({
        position: location,
        map: map,
        title: 'Business Home Location',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
              <defs>
                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
                </filter>
              </defs>
              <rect x="2" y="2" width="32" height="32" rx="6" ry="6" fill="#F8F7FF" stroke="#E0E7FF" stroke-width="1" filter="url(#shadow)"/>
              <g transform="translate(6, 6)">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#9333EA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#9333EA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(36, 36),
          anchor: new window.google.maps.Point(18, 36),
        },
        zIndex: 1000
      });
      setHomeMarker(marker);
      
      // Pan to the new home location
      map.panTo(location);
      map.setZoom(15);
    }
    
    // Try to save to database (only if we have valid IDs)
    if (businessId && userId) {
      try {
        const homePinData: BusinessHomePin = {
          lat: location.lat,
          lng: location.lng,
          address: selectedAddress || undefined
        };
        
        const success = await BusinessMapService.setHomePin(businessId, homePinData, userId);
        
        if (success) {
          console.log('Home pin saved to database successfully');
        } else {
          console.log('Database save failed, but pin set locally');
        }
      } catch (error) {
        console.log('Database not available, but pin set locally:', error instanceof Error ? error.message : 'Unknown error');
      }
    } else {
      console.log('Missing business ID or user ID, pin set locally only');
    }
    
    setIsLoading(false);
    setShowHomePinModal(false);
  };

  // Load existing home pin from database
  useEffect(() => {
    const loadHomePin = async () => {
      console.log('🏠 [SalesMap] Starting home pin load...');
      console.log('🏠 [SalesMap] Business ID:', businessId);
      
      if (!businessId) {
        console.log('🏠 [SalesMap] No business ID available, skipping database load');
        setHomePinLoaded(true);
        return;
      }

      try {
        console.log('🏠 [SalesMap] Calling BusinessMapService.getHomePin...');
        const existingHomePin = await BusinessMapService.getHomePin(businessId);
        console.log('🏠 [SalesMap] getHomePin result:', existingHomePin);
        
        if (existingHomePin) {
          console.log('🏠 [SalesMap] Setting home pin from database:', existingHomePin);
          setHomePin({
            lat: existingHomePin.lat,
            lng: existingHomePin.lng
          });
          if (existingHomePin.address) {
            setSelectedAddress(existingHomePin.address);
            console.log('🏠 [SalesMap] Set selected address:', existingHomePin.address);
          }
          console.log('🏠 [SalesMap] Home pin loaded from database:', existingHomePin);
        } else {
          console.log('🏠 [SalesMap] No existing home pin found in database');
        }
      } catch (error) {
        console.error('🏠 [SalesMap] Error loading home pin from database:', error);
      } finally {
        console.log('🏠 [SalesMap] Setting homePinLoaded to true');
        setHomePinLoaded(true);
      }
    };

    loadHomePin();
  }, [businessId]);

  // Load existing address markers from database
  useEffect(() => {
    if (!businessId || !map || !window.google?.maps) return;

    const loadAddressMarkers = async () => {
      try {
        setIsLoadingMarkers(true);
        const existingMarkers = await addressMarkerService.getMarkers(businessId);
        
        // Clear existing markers first
        addressMarkers.forEach(({ marker }) => {
          if (marker) marker.setMap(null);
        });

        // Define marker icons
        const markerIcons = {
          'not-home': { bgColor: '#F3F4F6', borderColor: '#9CA3AF', iconColor: '#6B7280', title: 'Not Home' },
          'booked': { bgColor: '#DCFCE7', borderColor: '#16A34A', iconColor: '#16A34A', title: 'Booked' },
          'comeback-later': { bgColor: '#FEF3C7', borderColor: '#D97706', iconColor: '#D97706', title: 'Comeback Later' },
          'interested': { bgColor: '#DBEAFE', borderColor: '#2563EB', iconColor: '#2563EB', title: 'Interested' },
          'estimate-sent': { bgColor: '#F7FEE7', borderColor: '#84CC16', iconColor: '#84CC16', title: 'Estimate Sent' },
          'bad-customer': { bgColor: '#FEE2E2', borderColor: '#DC2626', iconColor: '#DC2626', title: 'Bad Customer' },
          'customer': { bgColor: '#E0F2FE', borderColor: '#0284C7', iconColor: '#0284C7', title: 'Customer' }
        };

        // Create Google Maps markers for each database marker
        const newMarkers = existingMarkers.map(markerData => {
          const iconConfig = markerIcons[markerData.marker_type];
          
          const googleMarker = new window.google.maps.Marker({
            position: { lat: markerData.latitude, lng: markerData.longitude },
            map: map,
            title: iconConfig.title,
            icon: {
              url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                  <defs>
                    <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                      <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
                    </filter>
                  </defs>
                  <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="${iconConfig.bgColor}" stroke="${iconConfig.borderColor}" stroke-width="1" filter="url(#shadow)"/>
                  <g transform="translate(5, 5)">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="${iconConfig.iconColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="${iconConfig.iconColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    ${markerData.marker_type === 'not-home' ? '' : 
                      markerData.marker_type === 'booked' ? 
                        `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><path d="M14.5,5 L16,6.5 L21.5,2" stroke="white" stroke-width="3.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>` :
                      markerData.marker_type === 'comeback-later' ?
                        `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><circle cx="18" cy="5" r="5.5" fill="none" stroke="white" stroke-width="3"/><path d="M18,2 L18,5 L21,8" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                      markerData.marker_type === 'interested' ?
                        `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><circle cx="18" cy="2" r="2.5" fill="white"/><path d="M15,8 Q18,11 21,8" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                      markerData.marker_type === 'estimate-sent' ?
                        `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><rect x="14" y="2" width="8" height="6" fill="white" stroke="white" stroke-width="1" rx="1"/><path d="M15,3.5 L21,3.5 M15,5 L21,5 M15,6.5 L19,6.5" stroke="${iconConfig.iconColor}" stroke-width="1.5" stroke-linecap="round"/>` :
                      markerData.marker_type === 'customer' ?
                        `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><circle cx="18" cy="2.5" r="2.5" fill="white"/><path d="M12,10 Q18,6 24,10" stroke="white" stroke-width="3" fill="none" stroke-linecap="round"/>` :
                        `<circle cx="18" cy="5" r="7" fill="${iconConfig.iconColor}"/><path d="M14,3 Q18,1 22,3 Q22,5 18,7 Q14,5 14,3 Z" fill="white"/><circle cx="16" cy="3.5" r="0.8" fill="${iconConfig.iconColor}"/><circle cx="20" cy="3.5" r="0.8" fill="${iconConfig.iconColor}"/><path d="M15,8 L17,6 L19,6 L21,8" stroke="white" stroke-width="2" stroke-linecap="round"/>`
                    }
                  </g>
                </svg>
              `),
              scaledSize: new window.google.maps.Size(32, 32),
              anchor: new window.google.maps.Point(16, 32),
            },
            zIndex: 100
          });

          // Add click listener to marker for editing
          googleMarker.addListener('click', () => {
            handleMarkerClick({ marker: googleMarker, data: markerData });
          });

          return {
            marker: googleMarker,
            data: markerData
          };
        });

        setAddressMarkers(newMarkers);
        console.log(`Loaded ${newMarkers.length} address markers from database`);
      } catch (error) {
        console.error('Error loading address markers:', error);
      } finally {
        setIsLoadingMarkers(false);
      }
    };

    loadAddressMarkers();
  }, [businessId, map]); // Note: excluding addressMarkers to avoid infinite loop



  // Create marker when homePin is loaded from database
  useEffect(() => {
    if (homePin && map && window.google?.maps && !homeMarker) {
      const marker = new window.google.maps.Marker({
        position: homePin,
        map: map,
        title: 'Business Home Location',
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
              <defs>
                <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
                </filter>
              </defs>
              <rect x="2" y="2" width="32" height="32" rx="6" ry="6" fill="#F8F7FF" stroke="#E0E7FF" stroke-width="1" filter="url(#shadow)"/>
              <g transform="translate(6, 6)">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#9333EA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#9333EA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </g>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(36, 36),
          anchor: new window.google.maps.Point(18, 36),
        },
        zIndex: 1000
      });
      setHomeMarker(marker);
      console.log('Home marker created from loaded data');
    }
  }, [homePin, map, homeMarker]);

  // Initialize map (wait for home pin to load first)
  useEffect(() => {
    if (!mapRef.current || map || !window.google?.maps || !homePinLoaded) return;

    try {
      // Use home pin location if available, otherwise default location
      const defaultLocation = { lat: 43.6532, lng: -79.3832 }; // Toronto as default
      const mapCenter = homePin || defaultLocation;
      const mapZoom = homePin ? 15 : 10; // Zoom in closer if we have a home pin

      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: mapCenter,
        zoom: mapZoom,
        zoomControl: false,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        scaleControl: false,
        rotateControl: false,
        mapTypeId: 'hybrid',
        disableDefaultUI: true,
        // Force top-down view at all zoom levels
        tilt: 0,
        // Mobile: 1 finger = pan, 2 fingers = zoom | Desktop: 2 fingers = pan/zoom
        gestureHandling: isMobile ? 'greedy' : 'cooperative',
      });

      // Add zoom event listener to ensure tilt stays at 0
      mapInstance.addListener('zoom_changed', () => {
        if (mapInstance.getTilt() !== 0) {
          mapInstance.setTilt(0);
        }
      });

      setMap(mapInstance);
      
      if (homePin) {
        console.log('Map initialized directly with home pin location:', homePin);
      } else {
        console.log('Map initialized with default location (no home pin)');
      }
    } catch (error) {
      console.error('Error initializing Google Maps:', error);
    }
  }, [mapRef.current, map, homePin, homePinLoaded]);

  // Display existing home pin when map loads
  useEffect(() => {
    if (!map || !homePin || homeMarker || !window.google?.maps) return;

    const marker = new window.google.maps.Marker({
      position: homePin,
      map: map,
      title: 'Business Home Location',
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
            <defs>
              <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
              </filter>
            </defs>
            <rect x="2" y="2" width="32" height="32" rx="6" ry="6" fill="#F8F7FF" stroke="#E0E7FF" stroke-width="1" filter="url(#shadow)"/>
            <g transform="translate(6, 6)">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#9333EA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#9333EA" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(36, 36),
        anchor: new window.google.maps.Point(18, 36),
      },
      zIndex: 1000
    });
    
    setHomeMarker(marker);
  }, [map, homePin, homeMarker]);

  // Add map click listener for setting home pin or address markers
  useEffect(() => {
    if (!map || !window.google?.maps) return;

    const clickListener = map.addListener('click', (event: any) => {
      const location = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      };

      if (showHomePinModal) {
        // Handle home pin setting
        handleSetHomePin(location);
      } else if (isGeofencingMode) {
        // Handle geofence point
        handleAddGeofencePoint(location);
      } else {
        // Handle address marker
        setClickedLocation(location);
        setShowAddressModal(true);
      }
    });

    return () => {
      if (clickListener) {
        window.google.maps.event.removeListener(clickListener);
      }
    };
  }, [map, showHomePinModal, isGeofencingMode]);

  // Initialize Google Places Autocomplete
  useEffect(() => {
    if (!showHomePinModal || !window.google?.maps || !addressInputRef.current) return;

    const autocompleteInstance = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      fields: ['place_id', 'geometry', 'formatted_address']
    });

    autocompleteInstance.addListener('place_changed', () => {
      const place = autocompleteInstance.getPlace();
      if (place.geometry && place.geometry.location) {
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        setSelectedAddress(place.formatted_address || '');
        handleSetHomePin(location);
      }
    });

    setAutocomplete(autocompleteInstance);

    return () => {
      if (autocompleteInstance) {
        window.google.maps.event.clearInstanceListeners(autocompleteInstance);
      }
    };
  }, [showHomePinModal]);

  // Geofencing functions
  const handleToggleGeofencing = () => {
    if (isGeofencingMode) {
      // Exit geofencing mode - clear all geofence data
      handleClearGeofence();
    } else if (geofenceCompleted) {
      // If there's a completed geofence, clear it to start a new one
      handleClearGeofence();
    }
    setIsGeofencingMode(!isGeofencingMode);
  };

  const handleAddGeofencePoint = (location: {lat: number, lng: number}) => {
    if (!map || !window.google?.maps) return;

    const pointNumber = geofencePoints.length + 1;
    
    // Create numbered marker
    const marker = new window.google.maps.Marker({
      position: location,
      map: map,
      title: `Geofence Point ${pointNumber}`,
      label: {
        text: pointNumber.toString(),
        color: 'white',
        fontWeight: 'bold',
        fontSize: '12px'
      },
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="12" fill="#3B82F6" stroke="white" stroke-width="2"/>
            <text x="16" y="20" text-anchor="middle" font-family="Arial" font-size="10" font-weight="bold" fill="white">${pointNumber}</text>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(32, 32),
        anchor: new window.google.maps.Point(16, 16),
      },
      zIndex: 200
    });

    // Add to state
    setGeofencePoints(prev => [...prev, location]);
    setGeofenceMarkers(prev => [...prev, marker]);

    // Update polygon if we have at least 3 points
    const newPoints = [...geofencePoints, location];
    console.log(`Added point ${pointNumber}. Total points:`, newPoints.length, newPoints);
    
    if (newPoints.length >= 3) {
      console.log('Updating polygon with', newPoints.length, 'points');
      updateGeofencePolygon(newPoints);
    }
  };

  const updateGeofencePolygon = (points: {lat: number, lng: number}[]) => {
    if (!map || !window.google?.maps || points.length < 3) {
      console.log('Cannot create polygon:', { map: !!map, google: !!window.google?.maps, pointsLength: points.length });
      return;
    }

    console.log('Creating polygon with points:', points);

    // Remove existing polygon
    if (geofencePolygon) {
      geofencePolygon.setMap(null);
    }

    // Create new polygon
    const polygon = new window.google.maps.Polygon({
      paths: points,
      strokeColor: '#3B82F6',
      strokeOpacity: 0.8,
      strokeWeight: 3,
      fillColor: '#3B82F6',
      fillOpacity: 0.3,
      map: map,
      zIndex: 150
    });

    console.log('Polygon created:', polygon);
    setGeofencePolygon(polygon);
  };

  const handleClearGeofence = () => {
    // Remove all markers
    geofenceMarkers.forEach(marker => marker.setMap(null));
    
    // Remove polygon
    if (geofencePolygon) {
      geofencePolygon.setMap(null);
    }

    // Clear state
    setGeofencePoints([]);
    setGeofenceMarkers([]);
    setGeofencePolygon(null);
    setGeofenceName('');
    setIsEditingGeofenceName(false);
    setGeofenceCompleted(false);
  };

  // Load existing geofences from database
  const loadExistingGeofences = useCallback(async () => {
    if (!businessId || !map || !window.google?.maps) {
      console.log('Cannot load geofences - missing dependencies:', {
        businessId: !!businessId,
        map: !!map,
        googleMaps: !!window.google?.maps
      });
      return;
    }

    try {
      console.log('🔄 Loading existing geofences for business:', businessId);
      
      const geofences = await geofenceService.getGeofences(businessId);
      console.log('✅ Loaded geofences from database:', {
        count: geofences?.length || 0,
        geofences: geofences?.map(g => ({
          id: g.id,
          name: g.name,
          resourceCount: g.geofence_resources?.length || 0,
          resources: g.geofence_resources?.map(gr => ({
            id: gr.resource_id,
            name: `${gr.resources.first_name} ${gr.resources.last_name}`,
            email: gr.resources.email,
            role: gr.role
          })) || []
        }))
      });
      
      setSavedGeofences(geofences);

      // Clear existing loaded polygons
      console.log('🧹 Clearing existing polygons:', loadedGeofencePolygons.length);
      loadedGeofencePolygons.forEach(polygon => polygon.setMap(null));
      setLoadedGeofencePolygons([]);

      // Create polygons for each saved geofence
      const newPolygons: any[] = [];
      
      geofences.forEach((geofence, index) => {
        console.log(`🎯 Processing geofence ${index + 1}:`, {
          name: geofence.name,
          id: geofence.id,
          pointsCount: geofence.polygon_points?.length,
          resourceCount: geofence.geofence_resources?.length || 0,
          resources: geofence.geofence_resources?.map(gr => `${gr.resources.first_name} ${gr.resources.last_name}`) || [],
          points: geofence.polygon_points
        });

        if (geofence.polygon_points && geofence.polygon_points.length >= 3) {
          try {
            const polygon = new window.google.maps.Polygon({
              paths: geofence.polygon_points,
              strokeColor: '#10B981', // Green for saved geofences
              strokeOpacity: 0.8,
              strokeWeight: 3,
              fillColor: '#10B981',
              fillOpacity: 0.3,
              map: map,
              zIndex: 100 + index
            });

            // Add click listener for geofence info
            polygon.addListener('click', () => {
              console.log('🖱️ Clicked geofence:', geofence.name);
              // You could show an info window here
            });

            newPolygons.push(polygon);
            console.log(`✅ Created polygon for geofence: ${geofence.name}`);
          } catch (polygonError) {
            console.error(`❌ Error creating polygon for geofence ${geofence.name}:`, polygonError);
          }
        } else {
          console.warn(`⚠️ Skipping geofence ${geofence.name} - insufficient points:`, geofence.polygon_points?.length);
        }
      });

      setLoadedGeofencePolygons(newPolygons);
      console.log(`🎉 Successfully loaded ${newPolygons.length} geofence polygons on map`);
      
    } catch (error) {
      console.error('💥 Error loading geofences:', error);
    }
  }, [businessId, map]);

  // Load existing geofences when map is ready
  useEffect(() => {
    if (businessId && map && window.google?.maps) {
      console.log('🚀 Map ready, loading existing geofences...');
      loadExistingGeofences();
    }
  }, [businessId, map, loadExistingGeofences]);

  // Navigate to a specific geofence
  const handleNavigateToGeofence = (geofence: Geofence) => {
    if (!map || !window.google?.maps || !geofence.polygon_points || geofence.polygon_points.length === 0) {
      console.warn('Cannot navigate to geofence - missing map or points');
      return;
    }

    console.log('🗺️ Navigating to geofence:', geofence.name);

    try {
      // Create bounds to fit all points of the geofence
      const bounds = new window.google.maps.LatLngBounds();
      
      geofence.polygon_points.forEach(point => {
        bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
      });

      // Fit the map to the geofence bounds with some padding
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 100 }); // Extra padding on left for sidebar

      // Close the modal
      setShowGeofencesModal(false);

      console.log('✅ Successfully navigated to geofence:', geofence.name);
    } catch (error) {
      console.error('❌ Error navigating to geofence:', error);
    }
  };

  // Open edit modal for a geofence
  const handleEditGeofence = (geofence: Geofence, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation when clicking settings
    setEditingGeofence(geofence);
    setEditingName(geofence.name);
    setShowEditGeofenceModal(true);
  };

  // Delete a geofence with confirmation
  const handleDeleteGeofence = async (geofence: Geofence, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation when clicking delete
    
    // Confirm deletion
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${geofence.name}"?\n\nThis action cannot be undone and will remove all assigned resources from this geofence.`
    );
    
    if (!confirmDelete) {
      return;
    }

    try {
      console.log('🗑️ Deleting geofence:', geofence.name);

      await geofenceService.deleteGeofence(geofence.id!);

      console.log('✅ Geofence deleted successfully');

      // Remove from local state
      setSavedGeofences(prev => prev.filter(g => g.id !== geofence.id));

      // Remove from map if it exists
      const polygonToRemove = loadedGeofencePolygons.find(p => p.geofenceId === geofence.id);
      if (polygonToRemove) {
        polygonToRemove.polygon.setMap(null);
        setLoadedGeofencePolygons(prev => prev.filter(p => p.geofenceId !== geofence.id));
      }

    } catch (error) {
      console.error('❌ Error deleting geofence:', error);
      alert(`Failed to delete geofence: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // Save edited geofence name
  const handleSaveGeofenceEdit = async () => {
    if (!editingGeofence || !editingName.trim()) {
      return;
    }

    setIsSavingEdit(true);

    try {
      console.log('🔄 Updating geofence name:', editingGeofence.name, '->', editingName);

      const updatedGeofence = await geofenceService.updateGeofence(editingGeofence.id!, {
        name: editingName.trim()
      });

      console.log('✅ Geofence updated successfully:', updatedGeofence);

      // Update local state
      setSavedGeofences(prev => 
        prev.map(g => g.id === editingGeofence.id ? { ...g, name: editingName.trim() } : g)
      );

      // Close modal
      setShowEditGeofenceModal(false);
      setEditingGeofence(null);
      setEditingName('');

    } catch (error) {
      console.error('❌ Error updating geofence:', error);
      alert(`Failed to update geofence: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingEdit(false);
    }
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setShowEditGeofenceModal(false);
    setEditingGeofence(null);
    setEditingName('');
    setIsSavingEdit(false);
    setShowSelectResourceModal(false);
    setIsAddingResource(false);
    setIsRemovingResource(false);
  };

  // Handle adding resource to geofence
  const handleAddResourceToGeofence = async (resource: any) => {
    console.log('🎯 Starting handleAddResourceToGeofence with:', {
      resource: resource,
      editingGeofence: editingGeofence,
      businessId: businessId,
      timestamp: new Date().toISOString()
    });

    if (!editingGeofence || !businessId) {
      console.error('❌ Missing required data:', {
        hasEditingGeofence: !!editingGeofence,
        haBusinessId: !!businessId,
        editingGeofenceId: editingGeofence?.id,
        businessId: businessId
      });
      return;
    }

    setIsAddingResource(true);

    try {
      console.log('🔄 Adding resource to geofence:', {
        resourceName: `${resource.first_name} ${resource.last_name}`,
        resourceId: resource.id,
        geofenceId: editingGeofence.id,
        geofenceName: editingGeofence.name,
        businessId: businessId,
        role: 'assigned'
      });

      const result = await geofenceService.addResourceToGeofence(
        editingGeofence.id!,
        resource.id,
        businessId,
        'assigned'
      );

      console.log('✅ Resource added successfully:', result);

      // Refresh geofences to get updated data
      console.log('🔄 Refreshing geofences list...');
      await loadExistingGeofences();

      // Update editing geofence with new resource
      console.log('🔄 Fetching updated geofence data...');
      const updatedGeofences = await geofenceService.getGeofences(businessId!);
      const updatedGeofence = updatedGeofences.find(g => g.id === editingGeofence.id);
      
      if (updatedGeofence) {
        console.log('✅ Updated geofence found:', {
          geofenceId: updatedGeofence.id,
          resourceCount: updatedGeofence.geofence_resources?.length || 0,
          resources: updatedGeofence.geofence_resources?.map(gr => gr.resources.first_name + ' ' + gr.resources.last_name)
        });
        setEditingGeofence(updatedGeofence);
      } else {
        console.warn('⚠️ Updated geofence not found in response');
      }

      setShowSelectResourceModal(false);
      console.log('✅ Resource assignment completed successfully');

    } catch (error) {
      console.error('❌ Error adding resource to geofence:', {
        error: error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
        resourceId: resource.id,
        geofenceId: editingGeofence.id,
        businessId: businessId,
        timestamp: new Date().toISOString()
      });
      alert(`Failed to add resource: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsAddingResource(false);
      console.log('🏁 handleAddResourceToGeofence completed');
    }
  };

  // Handle removing resource from geofence
  const handleRemoveResourceFromGeofence = async (resourceId: string) => {
    if (!editingGeofence) {
      return;
    }

    if (!confirm('Remove this resource from the geofence area?')) {
      return;
    }

    setIsRemovingResource(true);

    try {
      console.log('🔄 Removing resource from geofence:', resourceId);

      await geofenceService.removeResourceFromGeofence(editingGeofence.id!, resourceId);

      console.log('✅ Resource removed successfully');

      // Refresh geofences to get updated data
      await loadExistingGeofences();

      // Update editing geofence with removed resource
      const updatedGeofences = await geofenceService.getGeofences(businessId!);
      const updatedGeofence = updatedGeofences.find(g => g.id === editingGeofence.id);
      if (updatedGeofence) {
        setEditingGeofence(updatedGeofence);
      }

    } catch (error) {
      console.error('❌ Error removing resource from geofence:', error);
      alert(`Failed to remove resource: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsRemovingResource(false);
    }
  };

  const handleCompleteGeofence = async () => {
    if (geofencePoints.length < 3) {
      alert('You need at least 3 points to create a geofence.');
      return;
    }

    if (!businessId || !userId) {
      alert('Missing business or user information. Please try again.');
      return;
    }

    console.log('Completing geofence with points:', geofencePoints);
    
    // Ensure the final polygon is created with all points
    updateGeofencePolygon(geofencePoints);
    
    // Set default name and mark as completed
    const defaultName = `Area ${savedGeofences.length + 1}`;
    setGeofenceName(defaultName);
    setGeofenceCompleted(true);
    
    // Save geofence to database
    try {
      setIsSavingGeofence(true);
      
      const geofenceData = {
        business_id: businessId,
        name: defaultName,
        description: `Service area with ${geofencePoints.length} boundary points`,
        polygon_points: geofencePoints,
        created_by: userId
      };

      console.log('Saving geofence to database:', geofenceData);
      
      const savedGeofence = await geofenceService.createGeofence(geofenceData);
      
      console.log('Geofence saved successfully:', savedGeofence);
      
      // Add to local state
      setSavedGeofences(prev => [savedGeofence, ...prev]);
      
      // Clear the current drawing polygon since it's now saved
      if (geofencePolygon) {
        geofencePolygon.setMap(null);
        setGeofencePolygon(null);
      }
      
      // Clear current markers
      geofenceMarkers.forEach(marker => marker.setMap(null));
      setGeofenceMarkers([]);
      setGeofencePoints([]);
      setGeofenceCompleted(false);
      
      // Reload geofences to display the newly saved one
      console.log('🔄 Reloading geofences to show newly saved area...');
      setTimeout(() => {
        loadExistingGeofences();
      }, 500);
      
      // Exit geofencing mode
      setIsGeofencingMode(false);
      
    } catch (error) {
      console.error('Error saving geofence:', error);
      alert(`Failed to save geofence: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSavingGeofence(false);
    }
  };

  return (
    <div className={`h-full w-full ${isMobile ? 'fixed inset-0 z-0' : ''}`}>
      <div className={`
        ${isMobile 
          ? 'h-screen w-screen' 
          : 'bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg h-[calc(100vh-200px)]'
        }
      `} style={!isMobile ? {
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)'
      } : {}}>
        <div className="h-full relative">
          <div ref={mapRef} className={`h-full w-full ${isMobile ? '' : 'rounded-2xl'} overflow-hidden`} />
          
          {/* Custom Menu Bar Overlay */}
          <div className={`absolute ${isMobile ? 'top-20 right-4' : 'top-20 left-4'} w-12 bg-white/95 backdrop-blur-xl rounded-xl border border-gray-200/50 shadow-lg z-10 flex flex-col p-2`} style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
            {/* Home Button */}
            <button 
              onClick={handleHomeClick}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-purple-50 hover:bg-purple-100 transition-all duration-200 active:scale-95 mb-2 shadow-sm" 
              title={homePin ? 'Go to home location' : 'Set home location'}
            >
              <Home className={`h-6 w-6 ${homePin ? 'text-purple-600' : 'text-purple-500'} stroke-2`} />
            </button>
            
            {/* Geofencing Button */}
            <button 
              onClick={handleToggleGeofencing}
              className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 active:scale-95 mb-2 shadow-sm ${
                isGeofencingMode 
                  ? 'bg-blue-100 hover:bg-blue-200 text-blue-600' 
                  : 'bg-gray-50 hover:bg-gray-100 text-gray-600'
              }`}
              title={isGeofencingMode ? 'Exit geofencing mode' : 'Create geofence'}
            >
              <Target className="h-5 w-5 stroke-2" />
            </button>
            
            {/* View Saved Geofences Button */}
            <div className="relative">
              <button 
                onClick={() => setShowGeofencesModal(true)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 active:scale-95 mb-2 shadow-sm ${
                  showGeofencesModal 
                    ? 'bg-purple-100 hover:bg-purple-200 text-purple-600' 
                    : 'bg-gray-50 hover:bg-gray-100 text-gray-600'
                }`}
                title={`View ${savedGeofences.length} saved geofence${savedGeofences.length !== 1 ? 's' : ''}`}
              >
                <Map className="h-5 w-5 stroke-2" />
              </button>
              {savedGeofences.length > 0 && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
                  {savedGeofences.length > 9 ? '9+' : savedGeofences.length}
                </div>
              )}
            </div>
            

            
            {/* Future menu items can be added here */}
          </div>
          
          {/* Geofencing Status Indicator */}
          {isGeofencingMode && (
            <div className={`absolute ${isMobile ? 'bottom-4 left-4 right-4' : 'bottom-4 right-4'} bg-purple-600 text-white rounded-xl shadow-lg z-10 ${isMobile ? '' : 'min-w-[320px]'} border border-purple-500/20`} style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              <div className="px-4 py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Target className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      Geofencing Mode ({geofencePoints.length} points)
                    </span>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 ml-3">
                    {geofencePoints.length >= 3 && (
                      <button 
                        onClick={handleCompleteGeofence}
                        className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-all duration-200 active:scale-95 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
                        title="Save geofence"
                        disabled={isSavingGeofence}
                      >
                        {isSavingGeofence ? (
                          <>
                            <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <span>💾</span>
                            <span>Save</span>
                          </>
                        )}
                      </button>
                    )}
                    
                    <button 
                      onClick={handleClearGeofence}
                      className="px-3 py-1.5 rounded-lg bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium transition-all duration-200 active:scale-95 shadow-sm flex items-center space-x-1"
                      title="Clear geofence"
                    >
                      <span>×</span>
                      <span>Clear</span>
                    </button>
                  </div>
                </div>
                
                {/* Status Text */}
                <div className="text-xs mt-2 opacity-90 text-purple-100">
                  {geofencePoints.length === 0 
                    ? 'Click on the map to add boundary points'
                    : geofencePoints.length < 3 
                      ? `Need ${3 - geofencePoints.length} more point${3 - geofencePoints.length > 1 ? 's' : ''} to create geofence`
                      : 'Ready to save! Click Save to store this area or Clear to start over'
                  }
                </div>
              </div>
            </div>
          )}


          
          {/* Geofence Name Display/Editor */}
          {geofenceCompleted && (
            <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-xl rounded-lg border border-gray-200/50 shadow-lg z-10 p-3" style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded border-2 border-white shadow-sm"></div>
                {isEditingGeofenceName ? (
                  <input
                    type="text"
                    value={geofenceName}
                    onChange={(e) => setGeofenceName(e.target.value)}
                    onBlur={() => setIsEditingGeofenceName(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setIsEditingGeofenceName(false);
                      }
                    }}
                    className="text-sm font-medium text-gray-800 bg-transparent border-none outline-none focus:ring-0 p-0 min-w-0"
                    autoFocus
                  />
                ) : (
                  <span 
                    className="text-sm font-medium text-gray-800 cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() => setIsEditingGeofenceName(true)}
                    title="Click to edit"
                  >
                    {geofenceName}
                  </span>
                )}
                <button
                  onClick={handleClearGeofence}
                  className="ml-2 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete geofence"
                >
                  <span className="text-xs font-bold">×</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Geofences Modal */}
      {showGeofencesModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Map className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Saved Geofences</h3>
                    <p className="text-sm text-gray-500">
                      {savedGeofences.length} area{savedGeofences.length !== 1 ? 's' : ''} saved
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowGeofencesModal(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                >
                  <span className="text-lg font-semibold">×</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {savedGeofences.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Map className="h-8 w-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No geofences saved</h4>
                  <p className="text-gray-500 mb-6">
                    Create your first geofence by clicking the Target icon and drawing an area on the map.
                  </p>
                  <button
                    onClick={() => {
                      setShowGeofencesModal(false);
                      setIsGeofencingMode(true);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Create Geofence
                  </button>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {savedGeofences.map((geofence, index) => (
                    <div
                      key={geofence.id}
                      onClick={() => handleNavigateToGeofence(geofence)}
                      className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-purple-200 cursor-pointer transition-all duration-200 group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                            <span className="text-sm font-semibold text-green-600">
                              {index + 1}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 group-hover:text-purple-600 transition-colors">
                              {geofence.name}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {geofence.polygon_points?.length || 0} boundary points
                              {geofence.geofence_resources && geofence.geofence_resources.length > 0 && 
                                ` • ${geofence.geofence_resources.length} resource${geofence.geofence_resources.length === 1 ? '' : 's'} assigned`
                              }
                              {geofence.description && ` • ${geofence.description}`}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Created {new Date(geofence.created_at || '').toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                            Active
                          </span>
                          <button
                            onClick={(e) => handleEditGeofence(geofence, e)}
                            className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-purple-600 transition-all duration-200 opacity-0 group-hover:opacity-100"
                            title="Edit geofence settings"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => handleDeleteGeofence(geofence, e)}
                            className="p-1.5 rounded-lg bg-gray-100 hover:bg-red-200 text-gray-600 hover:text-red-600 transition-all duration-200 opacity-0 group-hover:opacity-100"
                            title="Delete geofence"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {savedGeofences.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Click on any geofence to navigate to it on the map
                  </p>
                  <button
                    onClick={() => {
                      setShowGeofencesModal(false);
                      setIsGeofencingMode(true);
                    }}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                  >
                    Create New
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Geofence Modal */}
      {showEditGeofenceModal && editingGeofence && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Edit3 className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Geofence</h3>
                    <p className="text-sm text-gray-500">Update the area name</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseEditModal}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
                >
                  <span className="text-lg font-semibold">×</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="geofence-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Area Name
                  </label>
                  <input
                    id="geofence-name"
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isSavingEdit) {
                        handleSaveGeofenceEdit();
                      }
                      if (e.key === 'Escape') {
                        handleCloseEditModal();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter area name..."
                    autoFocus
                    disabled={isSavingEdit}
                  />
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span className="font-medium">Current area:</span>
                    <span>{editingGeofence.polygon_points?.length || 0} boundary points</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                    <span>Created:</span>
                    <span>{new Date(editingGeofence.created_at || '').toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Assigned Resources Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Assigned Resources
                    </label>
                    <button
                      onClick={() => setShowSelectResourceModal(true)}
                      className="flex items-center space-x-2 px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isSavingEdit || isAddingResource}
                    >
                      <UserPlus className="h-4 w-4" />
                      <span>Add Resource</span>
                    </button>
                  </div>

                  {editingGeofence.geofence_resources && editingGeofence.geofence_resources.length > 0 ? (
                    <div className="space-y-2">
                      {editingGeofence.geofence_resources.map((geofenceResource) => (
                        <div
                          key={geofenceResource.id}
                          className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                              <span className="text-sm font-semibold text-purple-600">
                                {geofenceResource.resources.first_name[0]}{geofenceResource.resources.last_name[0]}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {geofenceResource.resources.first_name} {geofenceResource.resources.last_name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {geofenceResource.resources.email}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveResourceFromGeofence(geofenceResource.resource_id)}
                            className="flex items-center space-x-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={isRemovingResource}
                            title="Remove resource"
                          >
                            <UserMinus className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                      <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No resources assigned to this area</p>
                      <p className="text-xs text-gray-400 mt-1">Click "Add Resource" to assign team members</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={handleCloseEditModal}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors"
                  disabled={isSavingEdit}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveGeofenceEdit}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  disabled={isSavingEdit || !editingName.trim()}
                >
                  {isSavingEdit ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Changes</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Select Resource Modal */}
      <SelectResourceModal
        isOpen={showSelectResourceModal}
        onClose={() => setShowSelectResourceModal(false)}
        onSelect={handleAddResourceToGeofence}
        selectedResources={editingGeofence?.geofence_resources?.map(gr => gr.resources) || []}
      />

      {/* Home Pin Modal */}
      {showHomePinModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-2xl w-full max-w-md" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
            <div className="p-6">
                            <div className="flex items-center space-x-3 mb-4">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                  <Home className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Set Home Location</h3>
                  <p className="text-sm text-gray-600">Search for an address or click anywhere on the map</p>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Address
                </label>
                <input
                  ref={addressInputRef}
                  type="text"
                  placeholder="Enter your business address..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-all duration-200"
                  value={selectedAddress}
                  onChange={(e) => setSelectedAddress(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    if (!isLoading) {
                      setShowHomePinModal(false);
                      setSelectedAddress('');
                    }
                  }}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Set to current map center as fallback
                    if (map && !isLoading) {
                      const center = map.getCenter();
                      handleSetHomePin({ lat: center.lat(), lng: center.lng() });
                    }
                  }}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 text-white bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed rounded-xl transition-all duration-200 font-medium"
                >
                  {isLoading ? 'Saving...' : 'Use Map Center'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Address Marker Modal */}
      {showAddressModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-2xl w-full max-w-md" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Mark Address</h3>
                  <p className="text-sm text-gray-600">Choose a status for this location</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-3 mb-6">
                <button
                  onClick={() => handlePlaceAddressMarker('not-home')}
                  disabled={isLoadingMarkers}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                >
                  <div className="mb-2">
                    <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                      <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="1"/>
                      <g transform="translate(5, 5)">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </g>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-800">Not Home</span>
                </button>
                
                <button
                  onClick={() => handlePlaceAddressMarker('booked')}
                  disabled={isLoadingMarkers}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-green-50 hover:border-green-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                >
                  <div className="mb-2">
                    <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                      <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#DCFCE7" stroke="#16A34A" strokeWidth="1"/>
                      <g transform="translate(5, 5)">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="18" cy="5" r="7" fill="#16A34A"/>
                        <path d="M14.5,5 L16,6.5 L21.5,2" stroke="white" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </g>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-green-700">Booked</span>
                </button>
                
                <button
                  onClick={() => handlePlaceAddressMarker('comeback-later')}
                  disabled={isLoadingMarkers}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-yellow-50 hover:border-yellow-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                >
                  <div className="mb-2">
                    <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                      <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#FEF3C7" stroke="#D97706" strokeWidth="1"/>
                      <g transform="translate(5, 5)">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="18" cy="5" r="7" fill="#D97706"/>
                        <circle cx="18" cy="5" r="5.5" fill="none" stroke="white" strokeWidth="3"/>
                        <path d="M18,2 L18,5 L21,8" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
                      </g>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-yellow-700">Comeback Later</span>
                </button>
                
                <button
                  onClick={() => handlePlaceAddressMarker('interested')}
                  disabled={isLoadingMarkers}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                >
                  <div className="mb-2">
                    <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                      <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#DBEAFE" stroke="#2563EB" strokeWidth="1"/>
                      <g transform="translate(5, 5)">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="18" cy="5" r="7" fill="#2563EB"/>
                        <circle cx="18" cy="2" r="2.5" fill="white"/>
                        <path d="M15,8 Q18,11 21,8" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
                      </g>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Interested</span>
                </button>
                
                <button
                  onClick={() => handlePlaceAddressMarker('estimate-sent')}
                  disabled={isLoadingMarkers}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-lime-50 hover:border-lime-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                >
                  <div className="mb-2">
                    <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                      <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#F7FEE7" stroke="#84CC16" strokeWidth="1"/>
                      <g transform="translate(5, 5)">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#84CC16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#84CC16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="18" cy="5" r="7" fill="#84CC16"/>
                        <rect x="14" y="2" width="8" height="6" fill="white" stroke="white" strokeWidth="1" rx="1"/>
                        <path d="M15,3.5 L21,3.5 M15,5 L21,5 M15,6.5 L19,6.5" stroke="#84CC16" strokeWidth="1.5" strokeLinecap="round"/>
                      </g>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-lime-700">Estimate Sent</span>
                </button>
                
                <button
                  onClick={() => handlePlaceAddressMarker('bad-customer')}
                  disabled={isLoadingMarkers}
                  className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-red-50 hover:border-red-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                >
                  <div className="mb-2">
                    <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                      <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#FEE2E2" stroke="#DC2626" strokeWidth="1"/>
                      <g transform="translate(5, 5)">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="18" cy="5" r="7" fill="#DC2626"/>
                        <path d="M14,3 Q18,1 22,3 Q22,5 18,7 Q14,5 14,3 Z" fill="white"/>
                        <circle cx="16" cy="3.5" r="0.8" fill="#DC2626"/>
                        <circle cx="20" cy="3.5" r="0.8" fill="#DC2626"/>
                        <path d="M15,8 L17,6 L19,6 L21,8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                      </g>
                    </svg>
                  </div>
                  <span className="text-sm font-medium text-gray-700 group-hover:text-red-700">Bad Customer</span>
                </button>
              </div>
              
              <button
                onClick={() => {
                  setShowAddressModal(false);
                  setClickedLocation(null);
                }}
                disabled={isLoadingMarkers}
                className="w-full px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 rounded-xl transition-all duration-200 font-medium"
              >
                {isLoadingMarkers ? 'Saving...' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Marker Modal */}
      {showEditMarkerModal && selectedMarkerForEdit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-2xl w-full max-w-md" style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}>
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-6">
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 flex items-center justify-center">
                  <Settings className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Edit Address Marker</h3>
                  <p className="text-sm text-gray-600">Change the marker type or delete it</p>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-4">Current marker type: <span className="font-medium text-gray-700">{selectedMarkerForEdit.data.marker_type.replace('-', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</span></p>
                
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => handleUpdateMarker('not-home')}
                    disabled={isUpdatingMarker || isDeletingMarker}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                  >
                    <div className="mb-2">
                      <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                        <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#F3F4F6" stroke="#9CA3AF" strokeWidth="1"/>
                        <g transform="translate(5, 5)">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </g>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-gray-800">Not Home</span>
                  </button>
                  
                  <button
                    onClick={() => handleUpdateMarker('booked')}
                    disabled={isUpdatingMarker || isDeletingMarker}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-green-50 hover:border-green-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                  >
                    <div className="mb-2">
                      <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                        <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#DCFCE7" stroke="#16A34A" strokeWidth="1"/>
                        <g transform="translate(5, 5)">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="18" cy="5" r="7" fill="#16A34A"/>
                          <path d="M14.5,5 L16,6.5 L21.5,2" stroke="white" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                        </g>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-green-700">Booked</span>
                  </button>
                  
                  <button
                    onClick={() => handleUpdateMarker('comeback-later')}
                    disabled={isUpdatingMarker || isDeletingMarker}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-yellow-50 hover:border-yellow-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                  >
                    <div className="mb-2">
                      <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                        <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#FEF3C7" stroke="#D97706" strokeWidth="1"/>
                        <g transform="translate(5, 5)">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="18" cy="5" r="7" fill="#D97706"/>
                          <circle cx="18" cy="5" r="5.5" fill="none" stroke="white" strokeWidth="3"/>
                          <path d="M18,2 L18,5 L21,8" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
                        </g>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-yellow-700">Comeback Later</span>
                  </button>
                  
                  <button
                    onClick={() => handleUpdateMarker('interested')}
                    disabled={isUpdatingMarker || isDeletingMarker}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                  >
                    <div className="mb-2">
                      <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                        <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#DBEAFE" stroke="#2563EB" strokeWidth="1"/>
                        <g transform="translate(5, 5)">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="18" cy="5" r="7" fill="#2563EB"/>
                          <circle cx="18" cy="2" r="2.5" fill="white"/>
                          <path d="M15,8 Q18,11 21,8" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round"/>
                        </g>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Interested</span>
                  </button>
                  
                  <button
                    onClick={() => handleUpdateMarker('estimate-sent')}
                    disabled={isUpdatingMarker || isDeletingMarker}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-lime-50 hover:border-lime-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                  >
                    <div className="mb-2">
                      <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                        <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#F7FEE7" stroke="#84CC16" strokeWidth="1"/>
                        <g transform="translate(5, 5)">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#84CC16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#84CC16" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="18" cy="5" r="7" fill="#84CC16"/>
                          <rect x="14" y="2" width="8" height="6" fill="white" stroke="white" strokeWidth="1" rx="1"/>
                          <path d="M15,3.5 L21,3.5 M15,5 L21,5 M15,6.5 L19,6.5" stroke="#84CC16" strokeWidth="1.5" strokeLinecap="round"/>
                        </g>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-lime-700">Estimate Sent</span>
                  </button>
                  
                  <button
                    onClick={() => handleUpdateMarker('bad-customer')}
                    disabled={isUpdatingMarker || isDeletingMarker}
                    className="flex flex-col items-center p-4 border border-gray-200 rounded-xl hover:bg-red-50 hover:border-red-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 group"
                  >
                    <div className="mb-2">
                      <svg width="32" height="32" viewBox="0 0 32 32" className="drop-shadow-sm">
                        <rect x="1" y="1" width="30" height="30" rx="5" ry="5" fill="#FEE2E2" stroke="#DC2626" strokeWidth="1"/>
                        <g transform="translate(5, 5)">
                          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <polyline points="9,22 9,12 15,12 15,22" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="18" cy="5" r="7" fill="#DC2626"/>
                          <path d="M14,3 Q18,1 22,3 Q22,5 18,7 Q14,5 14,3 Z" fill="white"/>
                          <circle cx="16" cy="3.5" r="0.8" fill="#DC2626"/>
                          <circle cx="20" cy="3.5" r="0.8" fill="#DC2626"/>
                          <path d="M15,8 L17,6 L19,6 L21,8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        </g>
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-red-700">Bad Customer</span>
                  </button>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowEditMarkerModal(false);
                    setSelectedMarkerForEdit(null);
                  }}
                  disabled={isUpdatingMarker || isDeletingMarker}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 rounded-xl transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteMarker}
                  disabled={isUpdatingMarker || isDeletingMarker}
                  className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed disabled:opacity-50 rounded-xl transition-all duration-200 font-medium flex items-center justify-center space-x-2"
                >
                  {isDeletingMarker ? (
                    <span>Deleting...</span>
                  ) : (
                    <>
                      <Archive className="h-4 w-4" />
                      <span>Delete</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Content wrapper components for marketing pages
function EmailCampaignsContent() {
  return <Campaigns />;
}

function LeadFormsContent() {
  return <LeadForms />;
}

function DiscountCodesContent() {
  return <DiscountCodes />;
}

function OnlineBookingsContent() {
  return <OnlineBookings />;
}

// Placeholder components for other marketing pages
function PlaceholderContent({ title, description }: { title: string; description: string }) {
  return (
        <div className="flex items-center justify-center h-96">
          <EmptyState
            icon={<Megaphone className="w-8 h-8 text-gray-400" />}
        title={`${title} - Coming Soon`}
        description={description}
        actionLabel="Back to Campaigns"
            onAction={() => {}}
          />
        </div>
  );
}

export default function Marketing() {
  const { canAccess } = usePermissionCheck();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // New state for mobile navigation pattern
  const [showMobileMenu, setShowMobileMenu] = useState(true);
  
  // Filter out disabled sub-pages based on permissions
  const availablePages = marketingPages.filter(page => {
    switch (page.id) {
      case 'campaigns':
        return canAccess.campaigns;
      case 'sales-map':
        return canAccess.salesMap;
      default:
        return true;
    }
  });
  
  const [activePage, setActivePage] = useState(() => {
    // Default to first available page
    return availablePages.length > 0 ? availablePages[0].id : 'campaigns';
  });

  // Check if current active page is still available, if not switch to first available
  React.useEffect(() => {
    if (!availablePages.some(page => page.id === activePage)) {
      const firstAvailable = availablePages.length > 0 ? availablePages[0].id : 'campaigns';
      setActivePage(firstAvailable);
    }
  }, [availablePages, activePage]);

  // Handle window resize
  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
        // Reset to menu view when switching to mobile
        setShowMobileMenu(true);
      } else {
        // On desktop, always show sidebar
        setSidebarOpen(true);
        setShowMobileMenu(false);
      }
    };

    handleResize(); // Call immediately
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle mobile navigation
  const handleMobilePageSelect = (pageId: string) => {
    setActivePage(pageId);
    if (isMobile) {
      setShowMobileMenu(false);
    }
  };

  // Track sales map state for FAB hiding on mobile
  useEffect(() => {
    const isOnSalesMap = isMobile && activePage === 'sales-map';
    localStorage.setItem('sales_map_open', isOnSalesMap.toString());
    
    // Dispatch custom event to notify FloatingActionButton
    window.dispatchEvent(new CustomEvent('salesMapChanged'));
  }, [isMobile, activePage]);

  const handleMobileBackToMenu = () => {
    if (isMobile) {
      setShowMobileMenu(true);
    }
  };

  const currentPage = availablePages.find(page => page.id === activePage) || availablePages[0] || marketingPages[0];

  const renderPageContent = () => {
    // Only render content for pages that are available
    const isPageAvailable = availablePages.some(page => page.id === activePage);
    
    if (!isPageAvailable) {
      // If no specific page is available, show first available or fallback
      if (availablePages.length > 0) {
        const firstAvailable = availablePages[0];
        switch (firstAvailable.id) {
          case 'campaigns':
            return (
              <div className="relative">
                <CampaignsContent />
                {/* Coming Soon Overlay - Only covers campaigns content */}
                <div className="absolute inset-0 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg z-10 flex items-center justify-center" style={{
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)'
                }}>
                  <div className="text-center p-8 max-w-md">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Megaphone className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Coming Soon</h3>
                    <p className="text-gray-600 mb-6">
                      Marketing campaigns are currently in development. We're working hard to bring you powerful campaign management tools.
                    </p>
                    <div className="bg-purple-50 rounded-lg p-4 mb-6">
                      <p className="text-sm text-purple-700 font-medium">
                        🚀 Features coming soon:
                      </p>
                      <ul className="text-sm text-purple-600 mt-2 space-y-1">
                        <li>• Email campaign management</li>
                        <li>• Social media campaigns</li>
                        <li>• Customer segmentation</li>
                        <li>• Campaign analytics & reporting</li>
                        <li>• Automated marketing workflows</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          case 'email-campaigns':
            return <EmailCampaignsContent />;
          case 'email-templates':
            return <EmailTemplatesContent />;
          case 'lead-forms':
            return <LeadFormsContent />;
          case 'discount-codes':
            return <DiscountCodesContent />;
          case 'online-bookings':
            return <OnlineBookingsContent />;
          case 'sales-map':
            return (
              <GoogleMapsLoader apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
                <SalesMapContent />
              </GoogleMapsLoader>
            );
          default:
            return <CampaignsContent />;
        }
      } else {
        return (
          <div className="relative">
            <CampaignsContent />
            {/* Coming Soon Overlay - Only covers campaigns content */}
            <div className="absolute inset-0 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg z-10 flex items-center justify-center" style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              <div className="text-center p-8 max-w-md">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Megaphone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Coming Soon</h3>
                <p className="text-gray-600 mb-6">
                  Marketing campaigns are currently in development. We're working hard to bring you powerful campaign management tools.
                </p>
                <div className="bg-purple-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-purple-700 font-medium">
                    🚀 Features coming soon:
                  </p>
                  <ul className="text-sm text-purple-600 mt-2 space-y-1">
                    <li>• Email campaign management</li>
                    <li>• Social media campaigns</li>
                    <li>• Customer segmentation</li>
                    <li>• Campaign analytics & reporting</li>
                    <li>• Automated marketing workflows</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      }
    }
    
    switch (activePage) {
      case 'campaigns':
        return (
          <div className="relative">
            <CampaignsContent />
            {/* Coming Soon Overlay - Only covers campaigns content */}
            <div className="absolute inset-0 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg z-10 flex items-center justify-center" style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              <div className="text-center p-8 max-w-md">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Megaphone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Coming Soon</h3>
                <p className="text-gray-600 mb-6">
                  Marketing campaigns are currently in development. We're working hard to bring you powerful campaign management tools.
                </p>
                <div className="bg-purple-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-purple-700 font-medium">
                    🚀 Features coming soon:
                  </p>
                  <ul className="text-sm text-purple-600 mt-2 space-y-1">
                    <li>• Email campaign management</li>
                    <li>• Social media campaigns</li>
                    <li>• Customer segmentation</li>
                    <li>• Campaign analytics & reporting</li>
                    <li>• Automated marketing workflows</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      case 'email-campaigns':
        return <EmailCampaignsContent />;
      case 'email-templates':
        return <EmailTemplatesContent />;
      case 'lead-forms':
        return <LeadFormsContent />;
      case 'discount-codes':
        return <DiscountCodesContent />;
      case 'online-bookings':
        return <OnlineBookingsContent />;
      case 'sales-map':
        return (
          <GoogleMapsLoader apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
            <SalesMapContent />
          </GoogleMapsLoader>
        );
      default:
        return (
          <div className="relative">
            <CampaignsContent />
            {/* Coming Soon Overlay - Only covers campaigns content */}
            <div className="absolute inset-0 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-lg z-10 flex items-center justify-center" style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}>
              <div className="text-center p-8 max-w-md">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Megaphone className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Coming Soon</h3>
                <p className="text-gray-600 mb-6">
                  Marketing campaigns are currently in development. We're working hard to bring you powerful campaign management tools.
                </p>
                <div className="bg-purple-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-purple-700 font-medium">
                    🚀 Features coming soon:
                  </p>
                  <ul className="text-sm text-purple-600 mt-2 space-y-1">
                    <li>• Email campaign management</li>
                    <li>• Social media campaigns</li>
                    <li>• Customer segmentation</li>
                    <li>• Campaign analytics & reporting</li>
                    <li>• Automated marketing workflows</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-gray-100 relative overflow-hidden" data-marketing-page style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      {/* Mobile Menu View - Full Screen */}
      {isMobile && showMobileMenu && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-gray-800 z-50 flex flex-col pt-16">
          {/* Mobile Menu Header */}
          <div className="p-6 border-b border-gray-700 bg-gray-800">
            <div className="flex items-center space-x-3">
              <div className="h-12 w-12 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg">
                <Megaphone className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Marketing</h2>
                <p className="text-sm text-gray-300">Campaign Management</p>
              </div>
            </div>
          </div>

          {/* Mobile Menu Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {availablePages.length > 0 ? (
              <nav className="space-y-3">
                {availablePages.map((page) => {
                  const Icon = page.icon;
                  const isActive = activePage === page.id;
                  
                  return (
                    <button
                      key={page.id}
                      onClick={() => handleMobilePageSelect(page.id)}
                      className={`w-full text-left p-4 rounded-xl transition-all duration-200 group ${
                        isActive
                          ? 'bg-gray-700 text-white shadow-sm'
                          : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-lg ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-600 text-gray-300 group-hover:bg-gray-500'
                        }`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <div className={`text-base font-medium ${
                            isActive ? 'text-white' : 'text-gray-200'
                          }`}>
                            {page.label}
                          </div>
                          <div className={`text-sm mt-1 ${
                            isActive ? 'text-gray-200' : 'text-gray-400'
                          }`}>
                            {page.description}
                          </div>
                        </div>
                        <ChevronRight className={`h-5 w-5 ${
                          isActive ? 'text-gray-300' : 'text-gray-400'
                        }`} />
                      </div>
                    </button>
                  );
                })}
              </nav>
            ) : (
              <div className="p-4 text-center text-gray-400">
                <p className="text-sm">No pages available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop/Mobile Content View */}
      {(!isMobile || !showMobileMenu) && (
        <>
          {/* Left Sidebar - Desktop Only */}
          {!isMobile && (
            <div className={`${
              sidebarOpen ? 'w-80' : 'w-12'
            } flex-shrink-0 transition-all duration-300 ease-in-out bg-gray-800 border-r border-gray-700 flex flex-col overflow-hidden relative shadow-xl rounded-r-2xl`}>
              
              {/* Toggle Button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="absolute top-4 right-4 p-2 bg-gray-700 rounded-xl hover:bg-gray-600 shadow-sm active:scale-95 z-20 transition-all duration-200"
                title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {sidebarOpen ? (
                  <ChevronLeft size={18} className="text-gray-300" />
                ) : (
                  <ChevronRight size={18} className="text-gray-300" />
                )}
              </button>

              {/* Header */}
              {sidebarOpen && (
                <div className="p-6 border-b border-gray-700">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg">
                      <Megaphone className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">Marketing</h2>
                      <p className="text-sm text-gray-300">Campaign Management</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Items */}
              {sidebarOpen && (
                <div className="flex-1 overflow-y-auto p-4">
                  {availablePages.length > 0 ? (
                    <nav className="space-y-2">
                      {availablePages.map((page) => {
                      const Icon = page.icon;
                      const isActive = activePage === page.id;
                      
                      return (
                        <button
                          key={page.id}
                          onClick={() => setActivePage(page.id)}
                          className={`w-full text-left p-3 rounded-xl transition-all duration-200 group ${
                            isActive
                              ? 'bg-gray-700 text-white shadow-sm'
                              : 'text-gray-300 hover:bg-gray-700 hover:text-white hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${
                              isActive
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-700 text-gray-300 group-hover:bg-gray-600 group-hover:text-white'
                            }`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1">
                              <div className={`text-sm font-medium ${
                                isActive ? 'text-white' : 'text-gray-300 group-hover:text-white'
                              }`}>
                                {page.label}
                              </div>
                              <div className={`text-xs ${
                                isActive ? 'text-gray-200' : 'text-gray-400 group-hover:text-gray-300'
                              }`}>
                                {page.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    </nav>
                  ) : (
                    <div className="p-4 text-center text-gray-400">
                      <p className="text-sm">No pages available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Mobile Content Header with Back Button - Hide for Sales Map */}
            {isMobile && !showMobileMenu && activePage !== 'sales-map' && (
              <div className="bg-gray-800 border-b border-gray-700 p-4 flex items-center">
                <button
                  onClick={handleMobileBackToMenu}
                  className="p-2 rounded-lg hover:bg-gray-700 mr-3 transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-300" />
                </button>
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-blue-600">
                    {React.createElement(currentPage.icon, {
                      className: "h-5 w-5 text-white"
                    })}
                  </div>
                  <div>
                    <h1 className="text-lg font-semibold text-white">{currentPage.label}</h1>
                    <p className="text-sm text-gray-300">{currentPage.description}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Mobile Back Button for Sales Map - Floating */}
            {isMobile && !showMobileMenu && activePage === 'sales-map' && (
              <div className="absolute top-4 left-4 z-20">
                <button
                  onClick={handleMobileBackToMenu}
                  className="p-3 rounded-full bg-white shadow-lg hover:bg-gray-50 transition-colors border border-gray-200"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            )}

            {/* Page Content */}
            <div className={`flex-1 ${isMobile && activePage === 'sales-map' ? 'absolute inset-0 z-0' : 'overflow-hidden'}`}>
              <div className="h-full overflow-y-auto">
                {renderPageContent()}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}